import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AIAssistant from './AIAssistant';
import InstallPrompt from './InstallPrompt';
import { 
  Home, 
  ClipboardList, 
  Wrench,
  BookOpen, 
  Info, 
  User, 
  LogOut,
  Menu,
  X,
  GraduationCap,
  X as XIcon,
  MessageCircle,
  Brain
} from 'lucide-react';

// 二维码弹窗组件
const QRModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-slate-800">与我们联系</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <XIcon className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="text-center">
            <div className="bg-slate-50 rounded-2xl p-4 mb-3">
              <img loading="lazy" decoding="async" src="/images/qrcodes/wechat.jpg" alt="微信" className="w-full rounded-xl" />
            </div>
            <p className="text-sm font-medium text-slate-600">微信公众号</p>
          </div>
          <div className="text-center">
            <div className="bg-slate-50 rounded-2xl p-4 mb-3">
              <img loading="lazy" decoding="async" src="/images/qrcodes/xiaohongshu.jpg" alt="小红书" className="w-full rounded-xl" />
            </div>
            <p className="text-sm font-medium text-slate-600">小红书</p>
          </div>
        </div>
        {/* 邮箱联系 */}
        <div className="mt-6 pt-6 border-t border-slate-100 text-center">
          <p className="text-sm text-slate-500 mb-2">或通过邮件联系我们</p>
          <a 
            href="mailto:support@psychbond.cn" 
            className="inline-flex items-center space-x-2 text-teal-600 hover:text-teal-700 font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>support@psychbond.cn</span>
          </a>
        </div>
      </div>
    </div>
  );
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [showQR, setShowQR] = useState(false);
  
  // 统一导航菜单
  const navigation = [
    { name: '首页', href: '/home', icon: Home },
    { name: '科普知识', href: '/knowledge', icon: GraduationCap },
    { name: '心理评估', href: '/assessments', icon: ClipboardList },
    { name: 'CBTs工具', href: '/tools', icon: Wrench },
    { name: 'DBT技能', href: '/dbt-skills', icon: Brain },
    { name: '心理视频', href: '/courses', icon: BookOpen },
    { name: '留言墙', href: '/guestbook', icon: MessageCircle },
    { name: '关于我们', href: '/about', icon: Info },
  ];
  
  // 处理导航点击
  const handleNavClick = (e, item) => {
    if (item.requireAuth && !user) {
      e.preventDefault();
      navigate('/login');
      return;
    }
  };

  const isActive = (path) => {
    if (location.pathname === path) return true;
    if (path === '/home' && (location.pathname === '/' || location.pathname === '/home')) return true;
    if (path === '/tools' && location.pathname.startsWith('/tools')) return true;
    if (path === '/dbt-skills' && (location.pathname.startsWith('/dbt-skills') || location.pathname.startsWith('/ro-dbt-skills'))) return true;
    if (path === '/knowledge' && location.pathname.startsWith('/knowledge')) return true;
    if (path === '/assessments' && location.pathname.startsWith('/assessments')) return true;
    if (path === '/courses' && location.pathname.startsWith('/courses')) return true;
    return false;
  };

  const isGreenTheme =
    location.pathname === '/' ||
    location.pathname === '/home' ||
    location.pathname === '/assessments' ||
    location.pathname.startsWith('/assessments/') ||
    location.pathname === '/knowledge' ||
    location.pathname.startsWith('/knowledge/') ||
    location.pathname === '/courses' ||
    location.pathname.startsWith('/courses/') ||
    location.pathname === '/guestbook' ||
    location.pathname === '/about' ||
    location.pathname === '/tools' ||
    location.pathname.startsWith('/tools/') ||
    location.pathname === '/dbt-skills' ||
    location.pathname.startsWith('/dbt-skills/') ||
    location.pathname === '/ro-dbt-skills' ||
    location.pathname.startsWith('/ro-dbt-skills/') ||
    location.pathname === '/dbt-diary-card';

  const isFullBleed =
    location.pathname === '/knowledge' ||
    location.pathname.startsWith('/knowledge/') ||
    location.pathname === '/assessments' ||
    location.pathname.startsWith('/assessments/') ||
    location.pathname === '/courses' ||
    location.pathname === '/tools' ||
    location.pathname.startsWith('/tools/') ||
    location.pathname === '/dbt-skills' ||
    location.pathname.startsWith('/dbt-skills/') ||
    location.pathname === '/ro-dbt-skills' ||
    location.pathname.startsWith('/ro-dbt-skills/') ||
    location.pathname === '/dbt-diary-card';

  return (
    <div className="min-h-screen bg-cream-50">
      {/* 顶部导航 */}
      <nav className={`sticky top-0 z-50 ${
        isGreenTheme
          ? 'bg-gradient-to-r from-emerald-700 via-teal-800 to-teal-900 shadow-md border-b border-white/10'
          : 'bg-white/80 backdrop-blur-md shadow-sm border-b border-cream-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/home" className="flex items-center space-x-2">
                <img 
                  src="/images/logo.png" 
                  alt="PsychBond" 
                  className="h-10 sm:h-[48px] w-auto object-contain"
                />
                <span className={`text-lg sm:text-xl font-bold ${
                  isGreenTheme
                    ? 'text-white'
                    : 'bg-gradient-to-r from-[#6BA89D] to-[#D4846C] bg-clip-text text-transparent'
                }`}>
                  PsychBond
                </span>
              </Link>
            </div>

            {/* 桌面端导航 */}
            <div className="hidden md:flex items-center space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                    isGreenTheme
                      ? isActive(item.href)
                        ? 'bg-white/20 text-white'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                      : isActive(item.href)
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-600 hover:text-primary-600 hover:bg-cream-100'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* 用户菜单 */}
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <>
                  <Link
                    to="/profile"
                    className={`flex items-center space-x-2 ${
                      isGreenTheme ? 'text-white/90 hover:text-white' : 'text-gray-600 hover:text-primary-600'
                    }`}
                    title="个人中心"
                  >
                    {user.avatar ? (
                      <img 
                        loading="lazy"
                        decoding="async"
                        src={user.avatar} 
                        alt="头像" 
                        className="w-8 h-8 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isGreenTheme ? 'bg-white/20' : 'bg-gradient-to-br from-primary-100 to-primary-200'
                      }`}>
                        <User className={`w-4 h-4 ${isGreenTheme ? 'text-white' : 'text-primary-500'}`} />
                      </div>
                    )}
                    <span className="text-sm">{user.username}</span>
                  </Link>
                  <button
                    onClick={logout}
                    className={`transition-colors ${
                      isGreenTheme ? 'text-white/80 hover:text-white' : 'text-gray-400 hover:text-red-500'
                    }`}
                    title="退出登录"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`text-sm font-medium ${
                      isGreenTheme ? 'text-white/90 hover:text-white' : 'text-gray-600 hover:text-primary-600'
                    }`}
                  >
                    登录
                  </Link>
                  <Link
                    to="/register"
                    className={`text-sm px-4 py-2 rounded-lg transition-colors font-medium ${
                      isGreenTheme
                        ? 'bg-white text-teal-600 hover:bg-white/90'
                        : 'bg-primary-500 text-white hover:bg-primary-600'
                    }`}
                  >
                    注册
                  </Link>
                </>
              )}
            </div>

            {/* 移动端：联系我们 + 菜单按钮 */}
            <div className="md:hidden flex items-center gap-2">
              <button 
                onClick={() => setShowQR(true)}
                className={`text-sm px-2 py-1 rounded-lg transition-colors ${
                  isGreenTheme
                    ? 'text-white/90 hover:text-white hover:bg-white/10'
                    : 'text-gray-600 hover:text-primary-600 hover:bg-gray-100'
                }`}
              >
                联系我们
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-1 ${isGreenTheme ? 'text-white hover:text-white/80' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* 移动端菜单 */}
        {mobileMenuOpen && (
          <div className={`md:hidden border-t ${
            isGreenTheme
              ? 'bg-gradient-to-br from-emerald-700 to-teal-900 border-white/10'
              : 'bg-white border-gray-200'
          }`}>
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={(e) => {
                      if (item.requireAuth && !user) {
                        e.preventDefault();
                        navigate('/login');
                        return;
                      }
                      setMobileMenuOpen(false);
                    }}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-base font-medium ${
                      isGreenTheme
                        ? isActive(item.href)
                          ? 'bg-white/20 text-white'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                        : isActive(item.href)
                          ? 'bg-primary-50 text-primary-600'
                          : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              
              {/* 移动端：用户菜单 */}
              <div className={`border-t pt-2 mt-2 ${isGreenTheme ? 'border-white/10' : 'border-gray-200'}`}>
                {user ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-base font-medium ${
                        isGreenTheme
                          ? 'text-white/90 hover:text-white hover:bg-white/10'
                          : 'text-gray-600 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      {user.avatar ? (
                        <img 
                          loading="lazy"
                          decoding="async"
                          src={user.avatar} 
                          alt="头像" 
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <User className="w-5 h-5" />
                      )}
                      <span>个人资料</span>
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-base font-medium ${
                        isGreenTheme
                          ? 'text-white/90 hover:text-white hover:bg-white/10'
                          : 'text-gray-600 hover:text-red-600 hover:bg-gray-50'
                      }`}
                    >
                      <LogOut className="w-5 h-5" />
                      <span>退出登录</span>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-2 font-medium ${
                      isGreenTheme ? 'text-white' : 'text-primary-600'
                    }`}
                  >
                    <User className="w-5 h-5" />
                    <span>登录 / 注册</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* 主要内容 */}
      <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isFullBleed ? 'pt-0 pb-10' : 'py-10'}`}>
        {children}
      </main>

      {/* 页脚 */}
      <footer className={`mt-4 ${
        isGreenTheme
          ? 'bg-gradient-to-r from-emerald-700 via-teal-800 to-teal-900 border-t border-white/10'
          : 'bg-white border-t border-cream-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          {/* 桌面端/平板端：左右布局 */}
          <div className="hidden sm:flex sm:justify-between sm:items-end">
            {/* 左侧：热线和版权 */}
            <div className={`text-left text-sm ${isGreenTheme ? 'text-white/80' : 'text-slate-400'}`}>
              <div className="flex flex-wrap gap-x-2 gap-y-1 mb-2">
                <span>心理危机援助热线：400-161-9995</span>
                <span>|</span>
                <span>12355 青少年服务台</span>
                <span>|</span>
                <span>北京心理危机研究与干预中心：010-82951332</span>
              </div>
              <div>
                <span>Copyright © 2026 心灵纽带（深圳）科技有限责任公司 · </span>
                <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" className={`transition-colors ${isGreenTheme ? 'hover:text-white' : 'hover:text-slate-600'}`}>粤ICP备2026031266号-1</a>
              </div>
            </div>
            
            {/* 右侧：联系我们按钮 */}
            <button 
              onClick={() => setShowQR(true)}
              className={`text-sm px-4 py-2 rounded-lg transition-colors font-medium mr-20 ${
                isGreenTheme
                  ? 'bg-white text-teal-600 hover:bg-white/90'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              }`}
            >
              联系我们
            </button>
          </div>
          
          {/* 移动端：保持居中布局 */}
          <div className="sm:hidden">
            {/* 第一行：心理危机援助热线 */}
            <div className={`text-center text-xs mb-3 ${isGreenTheme ? 'text-white/80' : 'text-slate-400'}`}>
              <div className="flex flex-col">
                <span>心理危机援助热线：400-161-9995</span>
                <span>12355 青少年服务台</span>
                <span>北京心理危机研究与干预中心：010-82951332</span>
              </div>
            </div>
            
            {/* 第二行：版权信息 */}
            <div className={`text-center text-xs ${isGreenTheme ? 'text-white/80' : 'text-slate-400'}`}>
              <span>
                Copyright © 2026 心灵纽带（深圳）科技有限责任公司 · <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600 transition-colors">粤ICP备2026031266号-1</a>
              </span>
            </div>
          </div>
        </div>
      </footer>
      
      {/* 二维码弹窗 */}
      <QRModal isOpen={showQR} onClose={() => setShowQR(false)} />
      
      {/* Meta AI助手 - 所有页面显示 */}
      <AIAssistant />
      <InstallPrompt />
    </div>
  );
};

export default Layout;
